import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import ReportCardView, { ReportCardDetails } from '../components/ReportCardView';
import Spinner from '../components/Spinner';

const SchoolWorkReportCard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [details, setDetails] = useState<ReportCardDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReportCard = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get('/school-work/report-card');
        setDetails(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load report card');
      } finally {
        setLoading(false);
      }
    };

    fetchReportCard();
  }, []);

  useEffect(() => {
    if (!details) return;
    const previousTitle = document.title;
    const newTitle = details.schoolName || previousTitle;
    document.title = newTitle;
    return () => {
      document.title = previousTitle;
    };
  }, [details]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <Spinner />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <style>{`
        @page {
          size: A4 portrait;
          margin: 12mm;
        }
        @media print {
          body * {
            visibility: hidden;
          }
          .report-card-print-area,
          .report-card-print-area * {
            visibility: visible;
          }
          .report-card-print-area {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            padding: 0;
            box-sizing: border-box;
            background: #fff;
          }
          .report-card-page {
            box-shadow: none !important;
            width: 100% !important;
            max-width: 210mm !important;
            margin: 0 auto !important;
            padding: 0 !important;
          }
          .report-card-page > div {
            padding: 0 !important;
          }
          .hide-on-print {
            display: none !important;
          }
        }
      `}</style>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Report Card Preview
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" color="primary" onClick={() => window.print()}>
            Print / Download
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate(location.pathname.startsWith('/parent') ? '/parent/dashboard' : '/school-work')}
          >
            {location.pathname.startsWith('/parent') ? 'Back to Dashboard' : 'Back to School Work'}
          </Button>
        </Box>
      </Box>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : details ? (
        <Box className="report-card-print-area">
          <ReportCardView details={details} />
        </Box>
      ) : (
        <Alert severity="info">No report card data is available yet.</Alert>
      )}
    </Box>
  );
};

export default SchoolWorkReportCard;
